import React from 'react';
import { useLocation } from 'react-router-dom';

const StructuredData = () => {
    const location = useLocation();

    const generateStructuredData = () => {
        const currentPath = location.pathname;
        
        // Base organization schema
        const organizationSchema = {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Business Directory",
            "url": "https://yourdomain.com",
            "logo": "https://yourdomain.com/logo.png",
            "description": "A comprehensive platform connecting communities with trusted local businesses. Find restaurants, healthcare, retail, automotive, entertainment, and services in your area.",
            "sameAs": [
                "https://facebook.com/businessdirectory",
                "https://twitter.com/businessdirectory",
                "https://linkedin.com/company/businessdirectory"
            ],
            "contactPoint": {
                "@type": "ContactPoint",
                "telephone": "+1-555-123-4567",
                "contactType": "customer service",
                "email": "support@businessdirectory.com",
                "availableLanguage": "English"
            },
            "address": {
                "@type": "PostalAddress",
                "streetAddress": "123 Business Street",
                "addressLocality": "City",
                "addressRegion": "State",
                "postalCode": "12345",
                "addressCountry": "US"
            }
        };

        // Website schema
        const websiteSchema = {
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "Business Directory",
            "url": "https://yourdomain.com",
            "description": "Find local businesses, services, and professionals in your area. Browse restaurants, healthcare, retail, automotive, entertainment, and services.",
            "potentialAction": {
                "@type": "SearchAction",
                "target": "https://yourdomain.com/search?q={search_term_string}",
                "query-input": "required name=search_term_string"
            }
        };

        // Page-specific schemas
        let pageSpecificSchema = null;

        if (currentPath === '/') {
            // Homepage - Local Business Directory
            pageSpecificSchema = {
                "@context": "https://schema.org",
                "@type": "ItemList",
                "name": "Local Business Directory",
                "description": "Comprehensive directory of local businesses including restaurants, healthcare, retail, automotive, entertainment, and services.",
                "url": "https://yourdomain.com",
                "numberOfItems": 500,
                "itemListElement": [
                    {
                        "@type": "ListItem",
                        "position": 1,
                        "item": {
                            "@type": "WebPage",
                            "name": "Restaurants",
                            "url": "https://yourdomain.com/restaurants"
                        }
                    },
                    {
                        "@type": "ListItem",
                        "position": 2,
                        "item": {
                            "@type": "WebPage",
                            "name": "Healthcare Services",
                            "url": "https://yourdomain.com/healthcare"
                        }
                    },
                    {
                        "@type": "ListItem",
                        "position": 3,
                        "item": {
                            "@type": "WebPage",
                            "name": "Retail Stores",
                            "url": "https://yourdomain.com/retail"
                        }
                    }
                ]
            };
        } else if (currentPath === '/restaurants') {
            // Restaurants page
            pageSpecificSchema = {
                "@context": "https://schema.org",
                "@type": "ItemList",
                "name": "Restaurants Near Me",
                "description": "Find the best restaurants in your area. Browse local dining options, read reviews, and discover new places to eat.",
                "url": "https://yourdomain.com/restaurants",
                "numberOfItems": 100,
                "itemListElement": [
                    {
                        "@type": "ListItem",
                        "position": 1,
                        "item": {
                            "@type": "Restaurant",
                            "name": "Local Restaurants",
                            "description": "Discover local restaurants in your area"
                        }
                    }
                ]
            };
        } else if (currentPath === '/healthcare') {
            // Healthcare page
            pageSpecificSchema = {
                "@context": "https://schema.org",
                "@type": "ItemList",
                "name": "Healthcare Services Near Me",
                "description": "Find trusted healthcare providers in your area. Browse doctors, dentists, specialists, and medical services.",
                "url": "https://yourdomain.com/healthcare",
                "numberOfItems": 50,
                "itemListElement": [
                    {
                        "@type": "ListItem",
                        "position": 1,
                        "item": {
                            "@type": "MedicalBusiness",
                            "name": "Healthcare Providers",
                            "description": "Find doctors, dentists, and medical professionals"
                        }
                    }
                ]
            };
        } else if (currentPath === '/about') {
            // About page
            pageSpecificSchema = {
                "@context": "https://schema.org",
                "@type": "AboutPage",
                "name": "About Business Directory",
                "description": "Learn about Business Directory's mission to connect communities with trusted local businesses.",
                "url": "https://yourdomain.com/about",
                "mainEntity": {
                    "@type": "Organization",
                    "name": "Business Directory",
                    "description": "Connecting communities with trusted local businesses",
                    "foundingDate": "2020",
                    "numberOfEmployees": "50+",
                    "location": {
                        "@type": "Place",
                        "name": "Business Directory Headquarters"
                    }
                }
            };
        } else if (currentPath === '/contact') {
            // Contact page
            pageSpecificSchema = {
                "@context": "https://schema.org",
                "@type": "ContactPage",
                "name": "Contact Business Directory",
                "description": "Get in touch with Business Directory for support, partnerships, or questions.",
                "url": "https://yourdomain.com/contact",
                "mainEntity": {
                    "@type": "Organization",
                    "name": "Business Directory",
                    "contactPoint": {
                        "@type": "ContactPoint",
                        "telephone": "+1-555-123-4567",
                        "contactType": "customer service",
                        "email": "support@businessdirectory.com",
                        "availableLanguage": "English"
                    }
                }
            };
        } else if (currentPath === '/faq') {
            // FAQ page
            pageSpecificSchema = {
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "name": "Business Directory FAQ",
                "description": "Find answers to frequently asked questions about Business Directory.",
                "url": "https://yourdomain.com/faq",
                "mainEntity": [
                    {
                        "@type": "Question",
                        "name": "What is Business Directory?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Business Directory is a comprehensive platform that connects users with local businesses. We help you discover trusted professionals, restaurants, services, and more in your area."
                        }
                    },
                    {
                        "@type": "Question",
                        "name": "How do I search for businesses?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "You can search for businesses using our search bar on the homepage. Enter keywords like business name, category, or location. You can also browse businesses by category or use our advanced filters."
                        }
                    },
                    {
                        "@type": "Question",
                        "name": "Is Business Directory free to use?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Yes! Business Directory is completely free for users to browse and search for businesses. Business owners can also list their businesses for free, with optional premium features available."
                        }
                    }
                ]
            };
        }

        // Breadcrumb schema for all pages
        const breadcrumbSchema = {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
                {
                    "@type": "ListItem",
                    "position": 1,
                    "name": "Home",
                    "item": "https://yourdomain.com"
                }
            ]
        };

        // Add page-specific breadcrumb
        if (currentPath !== '/') {
            const pageName = currentPath.substring(1).charAt(0).toUpperCase() + currentPath.substring(1).slice(1);
            breadcrumbSchema.itemListElement.push({
                "@type": "ListItem",
                "position": 2,
                "name": pageName,
                "item": `https://yourdomain.com${currentPath}`
            });
        }

        return [organizationSchema, websiteSchema, pageSpecificSchema, breadcrumbSchema].filter(Boolean);
    };

    const structuredData = generateStructuredData();

    return (
        <>
            {structuredData.map((data, index) => (
                <script
                    key={index}
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify(data, null, 2)
                    }}
                />
            ))}
        </>
    );
};

export default StructuredData; 